import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  collection, collectionGroup, onSnapshot, query, orderBy,
  doc, runTransaction, serverTimestamp, increment,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { useNewParam } from '../hooks/useNewParam';
import ReturnModal from '../components/ReturnModal';
import { ConsignmentPreviewModal } from '../components/ConsignmentDocument';
import { Button, Modal, ModalBanner, FormField, Dropdown, EntityCombobox, ConfirmModal } from '../components/ui';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { ChoiceButton, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { downloadCSV } from '../utils/csv';
import { buildEntry, postEntry, entryId, linesMemoOut } from '../utils/ledger';
import { deleteMemoRecord } from '../utils/memoDelete';
import type { InventoryItem, InventoryMemo, Entity, CodeHistoryEntry, CustomerMemo, CustomerMemoLineItem, BusinessSettings } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }

const PAGE_SIZE = 50;

// ── Helpers (duplicated per-page, matching this codebase's convention) ───────

function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
function round2(n: number) { return Math.round(n * 100) / 100; }
// Same formula as Inventory.tsx/Sales.tsx — carat minus whatever's actively
// held by concurrent memos, not the item's own status.
function availableCarat(item: InventoryItem): number {
  return round2(Math.max(0, item.carat - (item.memoCommittedCarat ?? 0)));
}
function stripUndef<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(v => stripUndef(v)) as unknown as T;
  if (obj !== null && typeof obj === 'object' && (obj as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndef(v)])
    ) as T;
  }
  return obj;
}
function daysFromToday(dateStr: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due   = new Date(dateStr + 'T00:00:00');
  return Math.floor((today.getTime() - due.getTime()) / 86_400_000);
}
function fmtDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
// A memo no. is stored in full ("CM-2026-0001"), but shown as just the
// prefix + last 4 digits — same convention as Purchase/Sales' lotNoDisplay.
// The full value is always what's saved; only the display is truncated.
function memoNoDisplay(memoNo?: string) {
  if (!memoNo) return '—';
  const m = memoNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : memoNo.slice(-4);
}

// ── Row model ─────────────────────────────────────────────────────────────────
// One row per memo commitment. Two sources feed this page:
//  - `InventoryMemo` docs (current model) — inventory/{id}/memos/{memoId},
//    one per client currently or previously holding part of a parcel.
//  - Legacy `InventoryItem` docs whose OLD single-memo fields are still
//    populated (written before per-item memos existed, when a full item
//    flipped straight to 'on-memo' or was sold directly from it) —
//    synthesized into the same row shape so this page renders both
//    uniformly. `legacyItem` is kept alongside so the Return action can
//    route to ReturnModal's old one-shot path for these.
type DisplayStatus = 'active' | 'returned' | 'sold';
const STATUS_RANK: Record<DisplayStatus, number> = { active: 0, sold: 1, returned: 2 };

interface MemoRow {
  key: string;
  data: InventoryMemo;
  legacyItem?: InventoryItem;
  displayStatus: DisplayStatus;
}

function legacyToRow(item: InventoryItem): MemoRow {
  const displayStatus: DisplayStatus =
    item.status === 'on-memo' ? 'active' : item.status === 'sold' ? 'sold' : 'returned';
  return {
    key: `legacy_${item.id}`,
    legacyItem: item,
    displayStatus,
    data: {
      id: `legacy_${item.id}`,
      businessId: item.businessId,
      inventoryItemId: item.id,
      entityId: item.memoToEntityId ?? '',
      shape: item.shape, color: item.color, clarity: item.clarity,
      sku: item.sku, code: item.code,
      carat: item.memoCarat ?? item.carat,
      pricePerCt: item.memoPricePerCt,
      amountUSD: item.memoAmountUSD,
      date: item.memoDate ?? '',
      returnByDate: item.memoReturnDate,
      status: displayStatus === 'active' ? 'active' : 'returned',
      createdAt: item.createdAt,
      createdBy: '',
    },
  };
}
function memoToRow(m: InventoryMemo): MemoRow {
  return { key: m.id, data: m, displayStatus: m.status === 'active' ? 'active' : 'returned' };
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconEmptyDiamond() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <polygon points="4,2 12,2 14.5,6 8,14.5 1.5,6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 14.5V6M1.5 6h13M4 2l4 4M12 2l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconExtend() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <circle cx="6.3" cy="7" r="5.3" stroke="currentColor" strokeWidth="1.3" />
    <path d="M6.3 4.2V7l2 1.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.8 1.8l1.7 1.7-1.7 1.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconPay() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="4" cy="9" r="1" fill="currentColor" />
  </svg>;
}
function IconExport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconPlus() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>;
}
function IconDots() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="3" r="1.3" fill="currentColor" />
      <circle cx="7" cy="7" r="1.3" fill="currentColor" />
      <circle cx="7" cy="11" r="1.3" fill="currentColor" />
    </svg>
  );
}
// ── Row actions — 3-dot Extend / Return menu, same pattern as Inventory.tsx
// and Entity.tsx's own RowActionsMenu — replaces two always-visible inline
// buttons with a single compact trigger. Extend is omitted for a legacy
// on-memo item, same as before (it has no per-item InventoryMemo doc to
// extend a return-by date on).
function RowActionsMenu({ onExtend, onReturn, onDelete }: { onExtend?: () => void; onReturn: () => void; onDelete?: () => void }) {
  return (
    <Dropdown
      align="right"
      trigger={() => (
        <button type="button" className="icon-btn-round" title="Actions">
          <IconDots />
        </button>
      )}
    >
      {onExtend && <button type="button" className="dropdown-item" onClick={onExtend}><IconExtend /> Extend</button>}
      <button type="button" className="dropdown-item" onClick={onReturn}>
        <IconPay /> Return
      </button>
      {onDelete && (
        <button type="button" className="dropdown-item danger" onClick={onDelete}>
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2.5 4h9M5.5 4V2.5h3V4M3.5 4l.6 8h5.8l.6-8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
          Delete (added by mistake)
        </button>
      )}
    </Dropdown>
  );
}
function IconSortArrow({ dir }: { dir: 'asc' | 'desc' }) {
  return <svg width="9" height="9" viewBox="0 0 10 10" fill="none"
    style={{ transform: dir === 'asc' ? 'rotate(180deg)' : undefined, transition: 'transform 0.12s' }}>
    <path d="M2 3.5L5 7l3-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── Sortable column header (same click-to-sort pattern as Purchase/Sales/
// Inventory/Entity — see SortableTh in Purchase.tsx) ─────────────────────────

type SortKey = 'code' | 'sku' | 'customer' | 'item' | 'carat' | 'price' | 'amount' | 'date' | 'returnBy' | 'status';

function SortableTh({ label, sortKey, activeKey, dir, onSort, align, filter }: {
  label: string; sortKey: SortKey; activeKey: SortKey; dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void; align?: 'right' | 'center';
  /** Excel-style filter button for this column (ColumnFilterButton). */
  filter?: React.ReactNode;
}) {
  const active = activeKey === sortKey;
  return (
    <th className={`th-sortable${active ? ' active' : ''}`} style={{ textAlign: align }} onClick={() => onSort(sortKey)}>
      <span className="th-sort-inner" style={{ justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
        {label}
        <span className="th-sort-arrow"><IconSortArrow dir={active ? dir : 'desc'} /></span>
        {filter}
      </span>
    </th>
  );
}

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity) ─────────────

function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);

  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }

  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> memo{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function MemoStatusBadge({ status }: { status: DisplayStatus }) {
  if (status === 'active') return (
    <span className="badge" style={{ background: 'var(--warning-bg)', color: 'var(--warning)', border: '1px solid var(--warning)' }}>On Memo</span>
  );
  if (status === 'sold') return (
    <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)' }}>Sold</span>
  );
  return (
    <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>Returned</span>
  );
}

// ── Detail modal header facts — matches AP / AR / Sales / Brokers ─────────────

function VDivider() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />;
}
function FactPanel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '7px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {children}
    </div>
  );
}
function MiniFact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>
        {value}
      </div>
    </div>
  );
}
function LotPill({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </div>
  );
}

const EVENT_LABEL: Record<CodeHistoryEntry['type'], string> = {
  'purchase':  'Purchased',
  'memo-out':  'Sent on Memo',
  'memo-in':   'Returned',
  'sale':      'Sold',
  'split-out': 'Split Out',
  'split-in':  'Split In',
  'return-out': 'Returned to Supplier',
  'return-in':  'Returned by Customer',
  'dummy-credit': 'Cash Credit (Books Adj.)',
  'dummy-debit':  'Cash Debit (Books Adj.)',
  'revaluation':  'Revalued',
};
const EVENT_COLOR: Record<CodeHistoryEntry['type'], string> = {
  'purchase':  'var(--primary)',
  'memo-out':  '#c2410c',
  'memo-in':   'var(--text-2)',
  'sale':      'var(--success)',
  'split-out': 'var(--warning)',
  'split-in':  'var(--primary)',
  'return-out': 'var(--danger)',
  'return-in':  'var(--success)',
  'dummy-credit': 'var(--danger)',
  'dummy-debit':  'var(--success)',
  'revaluation':  'var(--warning)',
};

// ── Extend modal — push out an active memo's return-by date ─────────────────

function ExtendMemoModal({ memo, onClose }: { memo: InventoryMemo; onClose: () => void }) {
  const { user } = useAuth();
  const [newDate, setNewDate] = useState(memo.returnByDate || todayISO());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    if (!newDate) return setError('Pick the new return-by date.');
    if (memo.returnByDate && newDate <= memo.returnByDate) {
      return setError('The new date must be later than the current one.');
    }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Not connected.'); setSaving(false); return; }
    const memoRef = doc(db, 'inventory', memo.inventoryItemId, 'memos', memo.id);
    const extRef  = doc(collection(db, 'inventory', memo.inventoryItemId, 'memos', memo.id, 'extensions'));
    try {
      await runTransaction(db, async tx => {
        const s = await tx.get(memoRef);
        if (!s.exists()) throw new Error('That memo no longer exists.');
        if ((s.data() as InventoryMemo).status !== 'active') throw new Error('That memo is no longer active.');
        const prev = (s.data() as InventoryMemo).returnByDate;
        tx.update(memoRef, { returnByDate: newDate, extensionCount: increment(1), updatedAt: serverTimestamp() });
        tx.set(extRef, {
          previousDate: prev ?? null, newDate, reason: reason.trim() || null,
          createdAt: serverTimestamp(), createdBy: user?.id ?? '',
        });
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not extend the memo.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} title="Extend Memo"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" icon={<IconExtend />} onClick={save} loading={saving}>Extend</Button>
      </>}>
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
        {memo.code ? <strong>{memo.code} · </strong> : null}{memo.shape} {memo.carat.toFixed(2)}ct — currently due back{' '}
        <strong>{fmtDate(memo.returnByDate)}</strong>{typeof memo.extensionCount === 'number' && memo.extensionCount > 0 ? ` (extended ${memo.extensionCount}×)` : ''}.
      </div>
      <div className="form-row-2">
        <FormField label="New return-by date" required>
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
        </FormField>
        <FormField label="Reason (optional)">
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. client still evaluating" />
        </FormField>
      </div>
    </Modal>
  );
}

// ── Row detail modal — this memo + the Code's full lifecycle ledger ──────────

function MemoDetailModal({
  row, onClose, onReturn, entities,
}: {
  row: MemoRow | null;
  onClose: () => void;
  onReturn: (row: MemoRow) => void;
  entities: Entity[];
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const [history,     setHistory]     = useState<CodeHistoryEntry[]>([]);
  const [histLoading,  setHistLoading] = useState(false);

  useEffect(() => {
    if (!row?.data.code) { setHistory([]); setHistLoading(false); return; }
    const db = getClientDb();
    if (!db) return;
    setHistLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'itemCodes', normalizeCode(row.data.code), 'history'), orderBy('date', 'desc')),
      snap => {
        // Newest first; same-day events by when they were recorded.
        const ms = (v: unknown) => (v && typeof (v as { toMillis?: () => number }).toMillis === 'function')
          ? (v as { toMillis: () => number }).toMillis() : 0;
        setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() } as CodeHistoryEntry))
          .sort((a, b) => b.date.localeCompare(a.date) || ms(b.createdAt) - ms(a.createdAt)));
        setHistLoading(false);
      },
      () => setHistLoading(false),
    );
    return unsub;
  }, [row?.data.code]);

  function entityName(id?: string) { return id ? (entities.find(e => e.id === id)?.companyName ?? '—') : '—'; }

  if (!row) return null;
  const m = row.data;

  return (
    <Modal
      open
      onClose={onClose}
      hideHead
      size="lg"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          {row.displayStatus === 'active' && (
            <Button variant="primary" icon={<IconPay />} onClick={() => { onClose(); onReturn(row); }}>
              Record Return
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      }
    >
      <ModalBanner
        title={entityName(m.entityId)}
        eyebrow="Memo Detail"
        right={
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Code
              </div>
              {m.code ? <LotPill>{m.code}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}
            </div>
            <VDivider />
            <MiniFact label="Memo Date" value={fmtDate(m.date)} />
            <VDivider />
            <MemoStatusBadge status={row.displayStatus} />
          </FactPanel>
        }
        onClose={onClose}
      />

      <SectionLabel>Memo Summary</SectionLabel>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1,
        border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
        overflow: 'hidden', marginBottom: 20,
      }}>
        {[
          { label: 'Item',       val: `${m.shape} ${m.color}/${m.clarity}` },
          { label: 'SKU',        val: m.sku || '—' },
          { label: 'Carat',      val: `${m.carat.toFixed(2)} ct` },
          { label: 'Price/ct',   val: m.pricePerCt ? money(m.pricePerCt) : '—' },
          { label: 'Amount',     val: m.amountUSD ? money(m.amountUSD) : '—' },
          { label: 'Return By',  val: fmtDate(m.returnByDate) },
        ].map(({ label, val }) => (
          <div key={label} style={{ background: 'var(--surface-2)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>{label}</div>
            <div style={{ fontWeight: 700, fontFamily: 'var(--font-num)', fontSize: 13, color: 'var(--text-1)' }}>{val}</div>
          </div>
        ))}
      </div>

      <SectionLabel>Code History — this stone's full lifecycle</SectionLabel>
      {!m.code ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '10px 0' }}>
          This item has no Code, so it has no lifecycle ledger.
        </div>
      ) : histLoading ? (
        <div className="skeleton" style={{ height: 60, borderRadius: 8 }} />
      ) : history.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '10px 0' }}>No events recorded yet.</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Date', 'Event', 'Customer', 'Carat', 'Price/ct', 'Amount'].map((h, i) => (
                  <th key={i} style={{ padding: '7px 12px', textAlign: i >= 3 ? 'right' : 'left', fontWeight: 600, color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.map((h, idx) => (
                <tr key={h.id} style={{ background: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{fmtDate(h.date)}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 700, color: EVENT_COLOR[h.type] }}>{EVENT_LABEL[h.type]}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-2)' }}>{entityName(h.entityId)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)' }}>{h.carat.toFixed(2)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{h.pricePerCt ? money(h.pricePerCt) : '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 600 }}>{h.amountUSD ? money(h.amountUSD) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// Shared table-cell input style — same values as Sales.tsx's own `TI`
// constant, used by every line-item table input across this app, so a
// carat/price box in this table looks identical to one in Sales'/Purchase's
// line-item tables instead of falling back to a bare unstyled <input>.
const TI: React.CSSProperties = {
  width: '100%', minWidth: 0, padding: '6px 9px', fontSize: 11,
  height: 32,
  border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
  background: 'var(--surface)', color: 'var(--text-1)',
  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};

// ── Send On Memo (multi-parcel) — the ONE place a multi-parcel consignment
// gets built, right here on the Memo Out page, rather than starting from a
// checkbox selection on the Inventory (stock) list. Earlier this flow
// started from Inventory; moved here so "send something on memo" always has
// exactly one starting point the user already associates with memos, and
// parcels are picked the same way Sales already lets you add stones to a
// sale — type a Code, see it match live, add it — rather than needing to
// pre-select rows somewhere else first.

// Code entry row — same interaction mechanics as Sales.tsx's CodeEntryRow
// (portaled live-suggestion panel, exact/unambiguous-prefix match, Enter to
// commit, Escape stops propagation so it only closes the panel and not the
// whole modal), adapted to this table's own column set.
// ── DescriptionCombobox — free text + a type-ahead list of the saved
// descriptions from Settings › Custom Options. Same widget and same
// BusinessSettings.saleLineDescriptions list Sales' own line items use —
// deliberately reused rather than a separate Memo-only preset list, per
// the explicit ask to keep this "same as Sales Invoice we take". Value IS
// the text (anything goes); the list is just quick picks. Duplicated here
// rather than imported — Sales.tsx's own copy is page-local, matching this
// app's per-page-helper convention. ─────────────────────────────────────
function DescriptionCombobox({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: string[];
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function update() {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = value.trim().toLowerCase();
  const list = options.filter(o => !q || o.toLowerCase().includes(q)).slice(0, 40);
  const hasList = options.length > 0 && list.length > 0;

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        style={{ ...TI, width: '100%' }}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onClick={() => options.length > 0 && setOpen(true)}
        onKeyDown={e => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}
        placeholder={options.length > 0 ? 'Add a note / pick…' : 'Add a note…'}
      />
      {open && pos && hasList && createPortal(
        <div ref={panelRef} className="dropdown-panel"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 240, overflowY: 'auto' }}>
          {list.map(o => (
            <button
              key={o} type="button" className="dropdown-item"
              onClick={() => { onChange(o); setOpen(false); inputRef.current?.blur(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', whiteSpace: 'normal' }}
            >
              {o}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function MemoCodeEntryRow({ available, onSelect }: { available: InventoryItem[]; onSelect: (item: InventoryItem) => void }) {
  const [q, setQ] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const draft = q.trim();
  const match = useMemo(() => {
    if (!draft) return null;
    const norm = draft.toUpperCase();
    const exact = available.find(i => (i.code ?? '').toUpperCase() === norm);
    if (exact) return exact;
    const partial = available.filter(i => (i.code ?? '').toUpperCase().startsWith(norm));
    return partial.length === 1 ? partial[0] : null;
  }, [available, draft]);

  const suggestions = useMemo(() => {
    const norm = draft.toUpperCase();
    return available
      .filter(i => !norm || (i.code ?? '').toUpperCase().includes(norm) || (i.sku ?? '').toUpperCase().includes(norm))
      .slice(0, 20);
  }, [available, draft]);

  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 300) });
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function commit() {
    if (!match) { setNotFound(!!draft); return; }
    onSelect(match);
    setQ(''); setNotFound(false); setOpen(false);
    inputRef.current?.focus();
  }
  function pick(item: InventoryItem) {
    onSelect(item);
    setQ(''); setNotFound(false); setOpen(false);
    inputRef.current?.focus();
  }

  const dim: React.CSSProperties = { color: match ? 'var(--text-1)' : 'var(--text-3)' };
  const entryTD: React.CSSProperties = { padding: '6px 10px', background: 'var(--primary-light)', fontSize: 12 };

  return (
    <tr>
      <td style={entryTD}>
        <input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setNotFound(false); setOpen(true); }}
          onClick={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape' && open) {
              // Stop the panel-close Escape from bubbling to the Modal's
              // own Escape-to-close handler (same guard Sales.tsx's
              // CodeEntryRow uses).
              e.stopPropagation();
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder="Type a Code…"
          autoComplete="off"
          style={{ ...TI, fontFamily: 'var(--font-num)', borderColor: notFound ? 'var(--danger)' : undefined }}
        />
        {notFound && <div style={{ fontSize: 9.5, color: 'var(--danger)', marginTop: 2, whiteSpace: 'nowrap' }}>No available item with that code</div>}
        {open && pos && createPortal(
          <div
            ref={panelRef}
            className="dropdown-panel"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 280, overflowY: 'auto' }}
          >
            <div className="dropdown-group-label">Code · SKU · Available</div>
            {suggestions.length === 0 && (
              <div style={{ padding: '8px 9px', fontSize: 12, color: 'var(--text-3)' }}>No available items match.</div>
            )}
            {suggestions.map(i => (
              <button key={i.id} type="button" className="dropdown-item" onClick={() => pick(i)}>
                <span style={{
                  display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
                  fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                  padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em', flexShrink: 0,
                }}>
                  {i.code || '—'}
                </span>
                <span style={{ fontFamily: 'var(--font-num)', fontSize: 11.5, color: 'var(--text-2)' }}>{i.sku || '—'}</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{i.shape} {i.color}/{i.clarity}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{availableCarat(i).toFixed(2)}ct</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
      </td>
      <td style={{ ...entryTD, fontFamily: 'var(--font-num)', ...dim }}>{match?.sku || '—'}</td>
      <td style={entryTD} />
      <td style={{ ...entryTD, ...dim }}>{match?.shape || '—'}</td>
      <td style={{ ...entryTD, ...dim }}>{match?.color || '—'}</td>
      <td style={{ ...entryTD, ...dim }}>{match?.clarity || '—'}</td>
      <td style={{ ...entryTD, ...dim }}>
        {match ? `${match.size || '—'}${match.subSize ? ` / ${match.subSize}` : ''}` : '—'}
      </td>
      <td style={{ ...entryTD, fontFamily: 'var(--font-num)', ...dim }}>{match ? availableCarat(match).toFixed(2) : '—'}</td>
      <td style={entryTD} />
      <td style={entryTD} />
      <td style={{ ...entryTD, textAlign: 'center' }}>
        {match && (
          <button type="button" onClick={commit} title="Add to this memo"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)', display: 'inline-flex' }}>
            <IconPlus />
          </button>
        )}
      </td>
    </tr>
  );
}

interface SendMemoRow { rowId: string; item: InventoryItem; carat: string; pricePerCt: string; description: string }
// Row identity is its own generated id, NOT item.id — the same Code/item can
// now be added more than once (split across two lines, e.g. different price
// points), so item.id can repeat across rows and can't be used as a React
// key or to address one specific row in updateRow/removeRow. Same simple
// incrementing-counter convention as Purchase.tsx's BatchItem._key.
let _rowKey = 0;
function nextRowId() { return String(++_rowKey); }
// Same cost/ct formula as StockRevaluation.tsx's own costPerCt helper —
// used to pre-fill a new row's Price/ct so the common case (memo out at
// cost) needs no typing; still fully editable afterward.
function costPerCt(i: InventoryItem) { return (i.carat ?? 0) > 0 ? round2((i.costUSD ?? 0) / i.carat) : 0; }

// The transaction below mirrors Inventory.tsx's own single-item MemoModal
// exactly, looped over every picked line, plus a grouping CustomerMemo doc,
// a sequential memo number (counters/{businessId}.customerMemo), and one
// ledger entry — same pattern MemoIn.tsx's ReceiveModal already established
// for the inbound side. Every read happens before any write (Firestore
// transaction requirement).
function SendMemoModal({ open, onClose, available, customers, onSaved }: {
  open: boolean;
  onClose: () => void;
  available: InventoryItem[];
  customers: Entity[];
  onSaved: (memo: CustomerMemo) => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const [entityId,   setEntityId]   = useState('');
  const [memoDate,   setMemoDate]   = useState(todayISO());
  const [returnDate, setReturnDate] = useState('');
  const [notes,      setNotes]      = useState('');
  const [rows,       setRows]       = useState<SendMemoRow[]>([]);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    if (!open) return;
    setEntityId(''); setMemoDate(todayISO()); setReturnDate(''); setNotes(''); setRows([]); setError('');
  }, [open]);

  // Carat already claimed by this item's OTHER rows in this draft memo — the
  // same Code can be added more than once (e.g. split across two price
  // points), so "how much of this item is left to add" has to account for
  // every row already holding some of it, not just whether any row exists.
  function claimedCarat(itemId: string, excludeRowId?: string) {
    return round2(rows
      .filter(r => r.item.id === itemId && r.rowId !== excludeRowId)
      .reduce((s, r) => s + (parseFloat(r.carat) || 0), 0));
  }
  function roomLeft(item: InventoryItem, excludeRowId?: string) {
    return round2(Math.max(0, availableCarat(item) - claimedCarat(item.id, excludeRowId)));
  }

  function addRow(item: InventoryItem) {
    const room = roomLeft(item);
    if (room <= 0) return; // this item's full available carat is already claimed by rows already in this memo
    const cost = costPerCt(item);
    setRows(rs => [...rs, { rowId: nextRowId(), item, carat: String(room), pricePerCt: cost > 0 ? String(cost) : '', description: '' }]);
  }
  function updateRow(rowId: string, patch: Partial<Pick<SendMemoRow, 'carat' | 'pricePerCt' | 'description'>>) {
    setRows(rs => rs.map(r => r.rowId === rowId ? { ...r, ...patch } : r));
  }
  function removeRow(rowId: string) {
    setRows(rs => rs.filter(r => r.rowId !== rowId));
  }

  // A Code stays pickable as long as it has ANY room left — including one
  // that already has a row, as long as that row hasn't claimed the whole
  // parcel. Only fully-claimed items drop out.
  const pickable = useMemo(
    () => available.filter(i => roomLeft(i) > 1e-9),
    [available, rows], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const totals = rows.reduce((acc, r) => {
    const caratNum = parseFloat(r.carat) || 0;
    return { ct: acc.ct + caratNum, amt: acc.amt + round2(caratNum * (parseFloat(r.pricePerCt) || 0)) };
  }, { ct: 0, amt: 0 });

  async function handleSave() {
    if (!entityId) { setError('Select a customer.'); return; }
    if (rows.length === 0) { setError('Add at least one parcel by its Code.'); return; }
    for (const r of rows) {
      const caratNum = parseFloat(r.carat) || 0;
      if (caratNum <= 0) { setError(`${r.item.shape}: enter a valid carat.`); return; }
      // Checked against this item's TOTAL available carat minus every OTHER
      // row already claiming part of it — not the raw available carat —
      // since the same Code can now span more than one row.
      const avail = availableCarat(r.item);
      const claimedByOtherRows = claimedCarat(r.item.id, r.rowId);
      if (caratNum + claimedByOtherRows > avail + 1e-6) {
        setError(`${r.item.shape}: only ${round2(avail - claimedByOtherRows).toFixed(2)}ct available across this Code's rows.`);
        return;
      }
    }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Database not connected.'); setSaving(false); return; }

    const memoRef     = doc(collection(db, 'customerMemos'));
    const itemRefs     = rows.map(r => doc(db, 'inventory', r.item.id));
    const counterRef   = doc(db, 'counters', business.id);
    let savedMemo: CustomerMemo | null = null;

    try {
      await runTransaction(db, async tx => {
        // ── Reads first (Firestore requires every read before any write) ──
        const counterSnap  = await tx.get(counterRef);
        const currentSnaps = await Promise.all(itemRefs.map(ref => tx.get(ref)));

        currentSnaps.forEach((snap, i) => {
          const r = rows[i];
          if (!snap.exists() || (snap.data() as InventoryItem).status !== 'in-stock') {
            throw new Error(`${r.item.shape}: changed status — please reload and try again.`);
          }
        });
        // Aggregate per item, not per row — the same Code can span more than
        // one row now, so the live check must be against the SUM every row
        // for that item is requesting, not each row against the item's full
        // available carat independently (which would let two rows on the
        // same item each pass individually while together over-claiming it).
        const requestedByItem = new Map<string, number>();
        rows.forEach(r => {
          const caratNum = parseFloat(r.carat) || 0;
          requestedByItem.set(r.item.id, round2((requestedByItem.get(r.item.id) ?? 0) + caratNum));
        });
        for (const [itemId, totalRequested] of requestedByItem) {
          const idx = rows.findIndex(r => r.item.id === itemId);
          const currentAvail = availableCarat(currentSnaps[idx].data() as InventoryItem);
          if (totalRequested > currentAvail + 1e-6) {
            throw new Error(`${rows[idx].item.shape}: only ${currentAvail.toFixed(2)}ct available now — please reload and try again.`);
          }
        }

        const prefix = (settings.memoOutPrefix || 'CM').toUpperCase();
        const memoN  = (counterSnap.exists() ? (counterSnap.data().customerMemo ?? 0) : 0) + 1;
        const memoNo = `${prefix}-${new Date().getFullYear()}-${String(memoN).padStart(4, '0')}`;

        // ── Writes — per-line InventoryMemo docs (identical shape to
        // Inventory.tsx's single-item MemoModal, plus customerMemoId), then
        // one grouping CustomerMemo doc, the counter, and the ledger entry.
        const lines: CustomerMemoLineItem[] = [];
        let totalCarat = 0, totalAmountUSD = 0, totalCostUSD = 0;

        rows.forEach((r, i) => {
          const current   = currentSnaps[i].data() as InventoryItem;
          const caratNum  = parseFloat(r.carat) || 0;
          const priceNum  = parseFloat(r.pricePerCt) || 0;
          const amountUSD = round2(caratNum * priceNum);
          const costUSD   = current.carat > 0 ? round2(current.costUSD * (caratNum / current.carat)) : 0;

          const inventoryMemoRef = doc(collection(db, 'inventory', r.item.id, 'memos'));
          tx.set(inventoryMemoRef, stripUndef({
            businessId: business.id, inventoryItemId: r.item.id, entityId,
            shape: current.shape, color: current.color, clarity: current.clarity,
            sku: current.sku, code: current.code, description: r.description.trim() || undefined,
            carat: caratNum, pricePerCt: priceNum || undefined, amountUSD: amountUSD || undefined,
            date: memoDate, returnByDate: returnDate || undefined,
            status: 'active' as const,
            customerMemoId: memoRef.id,
            createdAt: serverTimestamp(), createdBy: user!.id,
          }));

          if (current.code) {
            tx.set(doc(collection(db, 'itemCodes', normalizeCode(current.code), 'history')), stripUndef({
              type: 'memo-out', date: memoDate, carat: caratNum, entityId,
              pricePerCt: priceNum || undefined, amountUSD: amountUSD || undefined,
              inventoryItemId: r.item.id, createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }

          lines.push(stripUndef({
            id: inventoryMemoRef.id, inventoryItemId: r.item.id, inventoryMemoId: inventoryMemoRef.id,
            code: current.code, sku: current.sku, description: r.description.trim() || undefined,
            shape: current.shape, color: current.color, clarity: current.clarity,
            carat: caratNum, pricePerCt: priceNum || undefined, amountUSD: amountUSD || undefined,
            costUSD,
          }));
          totalCarat += caratNum; totalAmountUSD += amountUSD; totalCostUSD += costUSD;
        });

        // One memoCommittedCarat update per unique item, using the SUMMED
        // carat across all of that item's rows — multiple tx.update() calls
        // on the same doc ref within one transaction don't accumulate (each
        // sets an absolute value, the last one wins), so this has to be
        // aggregated rather than issued per-row when a Code has 2+ rows.
        for (const [itemId, totalRequested] of requestedByItem) {
          const idx = rows.findIndex(r => r.item.id === itemId);
          const current = currentSnaps[idx].data() as InventoryItem;
          tx.update(itemRefs[idx], {
            memoCommittedCarat: (current.memoCommittedCarat ?? 0) + totalRequested,
            updatedAt: serverTimestamp(),
          });
        }

        totalCarat = round2(totalCarat); totalAmountUSD = round2(totalAmountUSD); totalCostUSD = round2(totalCostUSD);

        const memoData: CustomerMemo = {
          id: memoRef.id, businessId: business.id, memoNo, toEntityId: entityId,
          date: memoDate, returnByDate: returnDate || undefined,
          lines, totalCarat, totalAmountUSD, totalCostUSD,
          status: 'open', notes: notes.trim() || undefined,
          createdAt: new Date().toISOString(), createdBy: user!.id,
        };
        tx.set(memoRef, stripUndef({ ...memoData, createdAt: serverTimestamp() }));
        tx.set(counterRef, { customerMemo: memoN }, { merge: true });

        if (totalCostUSD > 0.005) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'memo_out', sourceId: memoRef.id,
            sourceCollection: 'customerMemos', date: memoDate, currency: settings.currency, createdBy: user!.id,
            note: `Memo out ${memoNo}`,
            lines: linesMemoOut(totalCostUSD),
          }), entryId('memo_out', memoRef.id));
        }

        savedMemo = memoData;
      });
      if (savedMemo) onSaved(savedMemo);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open={open} onClose={onClose} title="Send On Memo"
      subtitle={`${rows.length} parcel${rows.length !== 1 ? 's' : ''} added`}
      size="xl"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Send {rows.length} Parcel{rows.length !== 1 ? 's' : ''} On Memo
            </Button>
          </div>
        </div>
      }
    >
      {/* ── Memo header — same bordered-box / compact-input layout as the Add
          Purchase / Sale invoice header and Memo In's "Consignment Header"
          (Purchase.tsx, Sales.tsx, MemoIn.tsx), not a bare row of plain
          inputs. ── */}
      <div className="form-section-label" style={{ marginBottom: 8 }}>Memo Header</div>
      <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 20 }}>
        <div className="form-table-compact" style={{ padding: '12px 14px 4px' }}>
          <div className="form-row-3" style={{ marginBottom: 0 }}>
            <FormField label="Customer" required>
              <EntityCombobox entities={customers} value={entityId} onChange={setEntityId} placeholder="Select customer…" />
            </FormField>
            <FormField label="Memo Date" required>
              <input type="date" value={memoDate} onChange={e => setMemoDate(e.target.value)} />
            </FormField>
            <FormField label="Expected Return Date">
              <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <FormField label="Notes (optional)">
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes…" />
              </FormField>
            </div>
          </div>
        </div>
      </div>

      <p className="form-hint" style={{ marginBottom: 8 }}>
        Type each parcel's Code below to add it — same as adding a stone to a sale.
      </p>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)' }}>
              <th style={{ width: 130, textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Code</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>SKU</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Description</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Shape</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Color</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Clarity</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Size</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Carat</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Price/ct</th>
              <th style={{ width: 120, textAlign: 'right', padding: '8px 10px', color: 'var(--text-3)', fontWeight: 600 }}>Amount</th>
              <th style={{ width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              // Room left FOR THIS ROW's own input = the item's full
              // available carat minus whatever this item's OTHER rows are
              // already claiming (not the flat item-level available carat) —
              // otherwise two rows on the same Code could each independently
              // claim the full amount.
              const roomForRow = round2(availableCarat(r.item) - claimedCarat(r.item.id, r.rowId));
              const caratNum = parseFloat(r.carat) || 0;
              const amt = round2(caratNum * (parseFloat(r.pricePerCt) || 0));
              return (
                <tr key={r.rowId} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-num)' }}>{r.item.code || <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}</td>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-num)' }}>{r.item.sku || <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}</td>
                  <td style={{ padding: '6px 10px', minWidth: 140 }}>
                    <DescriptionCombobox
                      value={r.description}
                      onChange={v => updateRow(r.rowId, { description: v })}
                      options={settings?.saleLineDescriptions ?? []}
                    />
                  </td>
                  <td style={{ padding: '6px 10px' }}>{r.item.shape}</td>
                  <td style={{ padding: '6px 10px' }}>{r.item.color}</td>
                  <td style={{ padding: '6px 10px' }}>{r.item.clarity}</td>
                  <td style={{ padding: '6px 10px' }}>
                    {r.item.size || <span style={{ opacity: 0.4 }}>—</span>}{r.item.subSize ? ` / ${r.item.subSize}` : ''}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <input
                      type="number" step="0.01" min="0.01" max={roomForRow} className="no-spinner"
                      value={r.carat} onChange={e => updateRow(r.rowId, { carat: e.target.value })}
                      style={{ ...TI, width: 66 }} title={`Max ${roomForRow.toFixed(2)}ct available`}
                    />
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <input
                      type="number" step="0.01" min="0" className="no-spinner"
                      value={r.pricePerCt} onChange={e => updateRow(r.rowId, { pricePerCt: e.target.value })}
                      placeholder="0.00" style={{ ...TI, width: 80 }}
                    />
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'var(--font-num)' }}>
                    {amt > 0 ? money(amt) : <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                    <button type="button" onClick={() => removeRow(r.rowId)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                      title="Remove from this memo">×</button>
                  </td>
                </tr>
              );
            })}
            <MemoCodeEntryRow available={pickable} onSelect={addRow} />
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                <td colSpan={7} style={{ padding: '8px 10px', fontWeight: 700, color: 'var(--text-2)' }}>Total</td>
                <td style={{ padding: '8px 10px', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{totals.ct.toFixed(2)}</td>
                <td />
                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(totals.amt)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Modal>
  );
}

// ── Consignments — one row per multi-parcel CustomerMemo, the grouping doc
// Send On Memo above creates on top of the ordinary per-item InventoryMemo
// docs the "By Item" tab above already lists individually. Entirely
// separate data source (`customerMemos`, not `collectionGroup 'memos'`)
// and its own compact filter/table — the existing "By Item" code above is
// untouched. ─────────────────────────────────────────────────────────────

function ConsignmentsSection({ entities, settings, business, statusFilter, customerFilter }: {
  entities: Entity[];
  settings: BusinessSettings | null;
  business: { businessName: string } | null;
  /** Filters live in the page header (Memos page owns them). */
  statusFilter: 'open' | 'closed' | 'all';
  customerFilter: string;
}) {
  const money = (v: number) => fmtMoney(v ?? 0, settings?.currency ?? 'USD');
  const [memos,   setMemos]   = useState<CustomerMemo[]>([]);
  const [loading, setLoading] = useState(true);
  // Stores just the id, not the memo object itself — the modal then looks
  // it up fresh from the live `memos` array on every render (same pattern
  // as Sales.tsx's own viewingInvoice). A static snapshot copy here would
  // never reflect a live Firestore write made from inside the modal (e.g.
  // the Invoice Options panel's accent-color swatch), so a selection would
  // silently write through to Firestore but the modal would keep showing
  // the pre-change value until closed and reopened.
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = detailId ? memos.find(m => m.id === detailId) ?? null : null;

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'customerMemos'), orderBy('createdAt', 'desc')),
      snap => { setMemos(snap.docs.map(d => ({ id: d.id, ...d.data() } as CustomerMemo))); setLoading(false); },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  function entityName(id?: string) { return id ? (entities.find(e => e.id === id)?.companyName ?? '—') : '—'; }

  const baseFiltered = useMemo(() => memos.filter(m => {
    if (statusFilter !== 'all' && m.status !== statusFilter) return false;
    if (customerFilter !== 'all' && m.toEntityId !== customerFilter) return false;
    return true;
  }), [memos, statusFilter, customerFilter]);

  // Excel-style filters on the consignment list too.
  const consFilterCols = useMemo<FilterColumn<CustomerMemo>[]>(() => [
    { key: 'memo',     label: 'Memo No',  get: m => memoNoDisplay(m.memoNo) },
    { key: 'customer', label: 'Customer', get: m => entityName(m.toEntityId) },
    { key: 'month',    label: 'Date',     get: m => m.date ? new Date(m.date + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '',
      sort: (a, b) => new Date('1 ' + a).getTime() - new Date('1 ' + b).getTime() },
    { key: 'status',   label: 'Status',   get: m => (m.status === 'open' ? 'Open' : m.status === 'closed' ? 'Closed' : String(m.status ?? '')) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows: filtered, filters: consFilters } = useColumnFilters(baseFiltered, consFilterCols);

  return (
    <>

      {loading ? (
        <div className="table-wrap">
          <table>
            <tbody>
              {[...Array(4)].map((_, i) => (
                <tr key={i} style={{ opacity: 1 - i * 0.18 }}>
                  <td style={{ padding: '10px 14px' }}><div className="skeleton" style={{ height: 13, width: 220, borderRadius: 4 }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : baseFiltered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: 'var(--text-3)' }}><IconEmptyDiamond /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No consignments yet</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Select multiple parcels on Inventory and choose "Send On Memo" to create one.
          </div>
        </div>
      ) : (<>
        {consFilters.active.length > 0 && (
          <div className="no-print" style={{ display: 'flex', marginBottom: 8 }}>
            <ColumnFilterChips filters={consFilters} inline />
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="th-filterable" style={{ width: 120 }}>Memo No<ColumnFilterButton filters={consFilters} colKey="memo" label="Memo No" /></th>
                <th className="th-filterable">Customer<ColumnFilterButton filters={consFilters} colKey="customer" label="Customer" /></th>
                <th style={{ width: 90 }}># Items</th>
                <th style={{ width: 110 }}>Total ct</th>
                <th style={{ width: 140 }}>Total Value</th>
                <th className="th-filterable" style={{ width: 130 }}>Date<ColumnFilterButton filters={consFilters} colKey="month" label="Date" /></th>
                <th className="th-filterable" style={{ width: 110 }}>Status<ColumnFilterButton filters={consFilters} colKey="status" label="Status" /></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id} onClick={() => setDetailId(m.id)} style={{ cursor: 'pointer' }}>
                  <td title={m.memoNo}><LotPill>{memoNoDisplay(m.memoNo)}</LotPill></td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{entityName(m.toEntityId)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{m.lines.length}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{m.totalCarat.toFixed(2)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(m.totalAmountUSD)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(m.date)}</td>
                  <td>
                    <span className="badge" style={m.status === 'open'
                      ? { background: 'var(--warning-bg)', color: '#c2410c', border: '1px solid var(--warning)' }
                      : { background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)' }}>
                      {m.status === 'open' ? 'Open' : 'Closed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}

      <ConsignmentPreviewModal
        open={!!detail}
        onClose={() => setDetailId(null)}
        memo={detail}
        entity={detail ? entities.find(e => e.id === detail.toEntityId) : undefined}
        settings={settings}
        business={business}
      />
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────


export default function MemosPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const [memoDocs,      setMemoDocs]      = useState<InventoryMemo[]>([]);
  const [legacyItems,   setLegacyItems]   = useState<InventoryItem[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [entities,    setEntities]    = useState<Entity[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [returnTarget, setReturnTarget] = useState<MemoRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MemoRow | null>(null);
  const [deleting,     setDeleting]     = useState(false);
  const [deleteError,  setDeleteError]  = useState('');
  const [extendTarget, setExtendTarget] = useState<MemoRow | null>(null);
  const [detailRow,    setDetailRow]    = useState<MemoRow | null>(null);
  const [viewMode, setViewMode] = useState<'items' | 'consignments'>('items');
  const [cStatus, setCStatus] = useState<'open' | 'closed' | 'all'>('open');
  const [showSendMemo, setShowSendMemo] = useState(false);
  // Dashboard "+ New" opens this form via ?new=1.
  useNewParam(() => setShowSendMemo(true));
  const [newMemo,       setNewMemo]      = useState<CustomerMemo | null>(null);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    // Current model — every InventoryMemo doc across every item, fetched via
    // a collection-group query (no filter/orderBy, so no composite index is
    // needed — sorting/filtering happens client-side, same as every other
    // list page in this app).
    const u1 = onSnapshot(
      collectionGroup(db, 'memos'),
      snap => {
        setMemoDocs(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryMemo)));
        setLoading(false);
      },
      () => setLoading(false),
    );
    // Legacy — items written before per-item memos existed still carry the
    // old single-memo fields residually; bridge them into the same view.
    const u2 = onSnapshot(
      query(collection(db, 'inventory'), orderBy('createdAt', 'desc')),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem));
        setLegacyItems(all.filter(i => !!i.memoToEntityId));
        setInventoryItems(all);
      },
      () => {},
    );
    const u3 = onSnapshot(
      query(collection(db, 'entities'), orderBy('companyName')),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
    );
    return () => { u1(); u2(); u3(); };
  }, []);

  const rows = useMemo(() => [
    ...memoDocs.map(memoToRow),
    ...legacyItems.map(legacyToRow),
  ].sort((a, b) => (b.data.date || '').localeCompare(a.data.date || '')), [memoDocs, legacyItems]);

  const customers = useMemo(() => entities.filter(e => e.types.includes('customer')), [entities]);
  function entityName(id?: string) { return id ? (entities.find(e => e.id === id)?.companyName ?? '—') : '—'; }

  // Parcels the Send On Memo picker can offer — same eligibility gate as
  // Inventory's own single-item action (in-stock, some carat still free).
  const sendMemoAvailable = useMemo(
    () => inventoryItems.filter(i => i.status === 'in-stock' && availableCarat(i) > 1e-9),
    [inventoryItems],
  );

  const activeRows  = useMemo(() => rows.filter(r => r.displayStatus === 'active'), [rows]);
  const overdueRows = useMemo(() => activeRows.filter(r => r.data.returnByDate && daysFromToday(r.data.returnByDate) > 0), [activeRows]);
  const overdueIds  = useMemo(() => new Set(overdueRows.map(r => r.key)), [overdueRows]);

  const byClient = useMemo(() => rows.filter(r => customerFilter === 'all' || r.data.entityId === customerFilter),
    [rows, customerFilter]);

  // Excel-style column filters (they replaced the Status header filter on
  // the By Item view). The page still opens on stones out on memo now.
  const STATUS_TEXT: Record<DisplayStatus, string> = { active: 'On Memo', returned: 'Returned', sold: 'Sold' };
  const filterColumns = useMemo<FilterColumn<MemoRow>[]>(() => [
    { key: 'sku',      label: 'SKU',       get: r => r.data.sku },
    { key: 'item',     label: 'Item',      get: r => r.data.shape },
    { key: 'returnBy', label: 'Return By', get: r => {
      if (r.displayStatus !== 'active' || !r.data.returnByDate) return '';
      const d = daysFromToday(r.data.returnByDate);
      return d > 0 ? 'Overdue' : d >= -7 ? 'Due within a week' : 'Later';
    }, sort: (a, b) => ['Overdue', 'Due within a week', 'Later'].indexOf(a) - ['Overdue', 'Due within a week', 'Later'].indexOf(b) },
    { key: 'status',   label: 'Status',    get: r => STATUS_TEXT[r.displayStatus],
      sort: (a, b) => ['On Memo', 'Returned', 'Sold'].indexOf(a) - ['On Memo', 'Returned', 'Sold'].indexOf(b) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);
  const { rows: filtered, filters: colFilters } = useColumnFilters(byClient, filterColumns, { status: ['On Memo'] });
  const filterable = new Set(filterColumns.map(c => c.key));

  // ── Sorting — click-to-sort table header, same pattern as Purchase/Sales ──
  function handleSort(key: SortKey) {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  }
  function compareRows(a: MemoRow, b: MemoRow): number {
    switch (sortBy) {
      case 'code':     return (a.data.code ?? '').localeCompare(b.data.code ?? '');
      case 'sku':      return (a.data.sku ?? '').localeCompare(b.data.sku ?? '');
      case 'customer': return entityName(a.data.entityId).localeCompare(entityName(b.data.entityId));
      case 'item':     return `${a.data.shape} ${a.data.color}/${a.data.clarity}`.localeCompare(`${b.data.shape} ${b.data.color}/${b.data.clarity}`);
      case 'carat':    return (a.data.carat ?? 0) - (b.data.carat ?? 0);
      case 'price':    return (a.data.pricePerCt ?? 0) - (b.data.pricePerCt ?? 0);
      case 'amount':   return (a.data.amountUSD ?? 0) - (b.data.amountUSD ?? 0);
      case 'date':     return (a.data.date ?? '').localeCompare(b.data.date ?? '');
      case 'returnBy': return (a.data.returnByDate ?? '').localeCompare(b.data.returnByDate ?? '');
      case 'status':   return STATUS_RANK[a.displayStatus] - STATUS_RANK[b.displayStatus];
    }
  }
  const sortedFiltered = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => sortDir === 'asc' ? compareRows(a, b) : compareRows(b, a));
    return arr;
  }, [filtered, sortBy, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  // Footer totals — grand totals of the whole filtered/sorted list (same
  // "whole filtered set, not just the current page" rule CSV export/other
  // tfoot totals in this app already follow), shown both on screen and
  // when printing.
  const footerTotals = useMemo(() => {
    const ct     = sortedFiltered.reduce((s, row) => s + (row.data.carat ?? 0), 0);
    const amount = sortedFiltered.reduce((s, row) => s + (row.data.amountUSD ?? 0), 0);
    return { ct, amount, avgPricePerCt: ct > 0 ? amount / ct : 0 };
  }, [sortedFiltered]);

  // ── Paging — same fixed 50-per-page Pagination control as every other
  // list page in the app. ──
  useEffect(() => { setCurrentPage(1); }, [customerFilter, sortBy, sortDir, colFilters.stamp]);
  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const pagedRows  = sortedFiltered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  // Printing the on-screen page would only print `PAGE_SIZE` rows — bypass
  // pagination for the print pass (every filtered/sorted memo instead), then
  // restore normal pagination once the print dialog closes. Waits for two
  // animation frames (not a fixed setTimeout) before calling print() — a
  // guessed delay is either wasted time on a short list or, worse, fires
  // before a long list has actually finished rendering; two rAFs guarantee
  // the browser has painted the just-swapped-in full list first, which is
  // what actually fixes "Print List feels slow to open."
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    window.addEventListener('afterprint', after);
    requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => { if (!cancelled) window.print(); });
    });
    return () => { cancelled = true; window.removeEventListener('afterprint', after); };
  }, [printAll]);
  const displayedRows = printAll ? sortedFiltered : pagedRows;

  const totals = useMemo(() => ({
    carat: activeRows.reduce((s, r) => s + r.data.carat, 0),
    value: activeRows.reduce((s, r) => s + (r.data.amountUSD ?? 0), 0),
    clients: new Set(activeRows.map(r => r.data.entityId)).size,
  }), [activeRows]);

  // ── Client-scoped summary — shown only once a single client is picked in
  // the filter above, so "open / overdue / total ct / total amount" reads
  // as that client's own numbers instead of the whole book. ──
  const clientStats = useMemo(() => {
    if (customerFilter === 'all') return null;
    const clientActive = activeRows.filter(r => r.data.entityId === customerFilter);
    return {
      open: clientActive.length,
      overdue: clientActive.filter(r => overdueIds.has(r.key)).length,
      carat: clientActive.reduce((s, r) => s + r.data.carat, 0),
      amount: clientActive.reduce((s, r) => s + (r.data.amountUSD ?? 0), 0),
    };
  }, [customerFilter, activeRows, overdueIds]);

  function openReturn(row: MemoRow) { setReturnTarget(row); }

  // Delete a parcel sent on memo by mistake. Undoes everything sending it
  // did, so no trace is left: the memo record, the carats it held on the
  // stone, its "Sent on Memo" line in the Code's history (Inventory
  // Activity), and its line in the consignment (whose ledger entry is
  // re-posted for what's left, or removed with the consignment if it was
  // the only line). Only for a memo still out, never returned or sold.
  async function confirmDelete() {
    const row = deleteTarget;
    const db = getClientDb();
    if (!row || !db) return;
    const m = row.data;
    setDeleting(true); setDeleteError('');
    try {
      await deleteMemoRecord(db, m, settings.currency);
      setDeleteTarget(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Could not delete this memo.');
    }
    setDeleting(false);
  }

  // ── Export — CSV of every filtered memo row (whole list, not just the
  // current page). Print bypasses pagination too (see printAll above) —
  // the @media print rules in index.css (shared by every list page) strip
  // the chrome and print the table as-is. ──
  function handleExportCSV() {
    downloadCSV(
      'memos',
      ['Code', 'SKU', 'Customer', 'Item', 'Carat', 'Price/ct', 'Amount', 'Memo Date', 'Return By', 'Status'],
      sortedFiltered.map(row => {
        const m = row.data;
        return [
          m.code ?? '', m.sku ?? '', entityName(m.entityId), `${m.shape} ${m.color}/${m.clarity}`,
          m.carat.toFixed(2), (m.pricePerCt ?? 0).toFixed(2), (m.amountUSD ?? 0).toFixed(2),
          m.date ?? '', m.returnByDate ?? '', row.displayStatus,
        ];
      }),
    );
  }

  return (
    <AppLayout
      title="Memo Out" subtitle="Goods sent out on memo — track what's out, overdue, and returned"
      toolbar={<>
        {/* View first (By Item / Consignments), then the client, then status. */}
        <div className="tb-seg" role="tablist" aria-label="View">
          {([
            { key: 'items' as const,        label: 'By Item' },
            { key: 'consignments' as const, label: 'Consignments' },
          ]).map(({ key, label }) => (
            <button key={key} type="button" role="tab" aria-selected={viewMode === key}
              className={viewMode === key ? 'on' : undefined} onClick={() => setViewMode(key)}>{label}</button>
          ))}
        </div>
        {/* .form-group only borrows the shared input look EntityCombobox needs. */}
        <div className="form-group tb-combo">
          <EntityCombobox
            entities={customers}
            value={customerFilter === 'all' ? '' : customerFilter}
            onChange={id => setCustomerFilter(id || 'all')}
            clearLabel="All Clients"
            placeholder="Search clients…"
          />
        </div>
        {viewMode === 'consignments' && (
          <ChoiceButton<'open' | 'closed' | 'all'>
            label="Status" value={cStatus} onChange={setCStatus} defaultKey="open"
            options={[
              { key: 'open',   label: 'Open',   dot: 'var(--primary)' },
              { key: 'closed', label: 'Closed', dot: 'var(--text-3)' },
              { key: 'all',    label: 'All' },
            ]}
          />
        )}
        {(customerFilter !== 'all' || (viewMode === 'items' ? colFilters.active.length > 0 : cStatus !== 'open')) && (
          <button type="button" className="tb-clear" onClick={() => { setCustomerFilter('all'); colFilters.clear(); setCStatus('open'); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        {viewMode === 'items' && (
          <div className="tb-group">
            <Dropdown
              align="right"
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExport /></button>
              )}
            >
              <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
              <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
            </Dropdown>
          </div>
        )}
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setShowSendMemo(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>Send On Memo</Button>
      </>}
    >
      {viewMode === 'consignments' ? (
        <ConsignmentsSection entities={entities} settings={settings} business={business}
          statusFilter={cStatus} customerFilter={customerFilter} />
      ) : (
      <>

      {/* Figures: the chosen client's, or everyone's when no client is picked;
          the column filter chips sit at the right end of the same row. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 8, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 auto' }}>
      {clientStats ? (
        <StatChips items={[
          { label: 'Open', value: String(clientStats.open) },
          { label: 'Overdue', value: String(clientStats.overdue), tone: clientStats.overdue > 0 ? 'danger' : undefined },
          { label: 'Total ct', value: `${clientStats.carat.toFixed(2)} ct` },
          { label: 'Total Amount', value: money(clientStats.amount) },
        ]} />
      ) : (
        <StatChips items={[
          { label: 'Active memos', value: `${activeRows.length} · ${totals.clients} client${totals.clients !== 1 ? 's' : ''}` },
          { label: 'Carat out', value: `${totals.carat.toFixed(2)} ct · ${money(totals.value)}` },
          { label: 'Overdue', value: String(overdueRows.length), tone: overdueRows.length > 0 ? 'danger' : undefined },
          { label: 'All records', value: String(rows.length) },
        ]} />
      )}
      </div>
      <ColumnFilterChips filters={colFilters} inline />
      </div>

      {/* ── Table / states ── */}
      {loading ? (
        <div className="table-wrap">
          <table>
            <thead><tr>{[90, 70, 140, 130, 60, 70, 80, 90, 75].map((w, i) => (
              <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
            ))}</tr></thead>
            <tbody>{[...Array(5)].map((_, i) => (
              <tr key={i} style={{ opacity: 1 - i * 0.18 }}>
                {[70, 60, 110, 100, 50, 60, 70, 80, 60].map((w, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 13, width: w, borderRadius: 4 }} /></td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: 'var(--text-3)' }}><IconEmptyDiamond /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
            {rows.length === 0 ? 'No memo history yet'
              : colFilters.active.length > 0 || customerFilter !== 'all' ? 'Nothing matches these filters'
              : 'Nothing out on memo right now'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Send an item on memo from the Inventory page — it'll show up here.
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortableTh label="Code" sortKey="code" activeKey={sortBy} dir={sortDir} onSort={handleSort} />
                <SortableTh label="SKU" sortKey="sku" activeKey={sortBy} dir={sortDir} onSort={handleSort}
                  filter={filterable.has('sku') ? <ColumnFilterButton filters={colFilters} colKey="sku" label="SKU" /> : undefined} />
                <SortableTh label="Customer" sortKey="customer" activeKey={sortBy} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Item" sortKey="item" activeKey={sortBy} dir={sortDir} onSort={handleSort}
                  filter={filterable.has('item') ? <ColumnFilterButton filters={colFilters} colKey="item" label="Item" /> : undefined} />
                <SortableTh label="Carat" sortKey="carat" activeKey={sortBy} dir={sortDir} onSort={handleSort} align="right" />
                <SortableTh label="Price/ct" sortKey="price" activeKey={sortBy} dir={sortDir} onSort={handleSort} align="right" />
                <SortableTh label="Amount" sortKey="amount" activeKey={sortBy} dir={sortDir} onSort={handleSort} align="right" />
                <SortableTh label="Memo Date" sortKey="date" activeKey={sortBy} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Return By" sortKey="returnBy" activeKey={sortBy} dir={sortDir} onSort={handleSort}
                  filter={filterable.has('returnBy') ? <ColumnFilterButton filters={colFilters} colKey="returnBy" label="Return By" /> : undefined} />
                <SortableTh label="Status" sortKey="status" activeKey={sortBy} dir={sortDir} onSort={handleSort}
                  filter={filterable.has('status') ? <ColumnFilterButton filters={colFilters} colKey="status" label="Status" /> : undefined} />
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map(row => {
                const m         = row.data;
                const days      = m.returnByDate ? daysFromToday(m.returnByDate) : null;
                const overdue   = row.displayStatus === 'active' && days !== null && days > 0;
                const dueSoon   = row.displayStatus === 'active' && days !== null && !overdue && days >= -7;
                return (
                  <tr key={row.key} onClick={() => setDetailRow(row)} style={{ cursor: 'pointer' }}>
                    <td>
                      {m.code ? <LotPill>{m.code}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                      {m.sku || <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{entityName(m.entityId)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {m.shape} {m.color}/{m.clarity}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{m.carat.toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                      {m.pricePerCt ? money(m.pricePerCt) : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>
                      {m.amountUSD ? money(m.amountUSD) : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(m.date)}</td>
                    <td>
                      {row.displayStatus !== 'active' ? (
                        <span style={{ fontSize: 12, opacity: 0.4 }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: overdue ? 'var(--danger)' : dueSoon ? '#c2410c' : 'var(--text-1)' }}>
                            {fmtDate(m.returnByDate)}
                          </span>
                          {days !== null && (
                            <span style={{ fontSize: 10, fontWeight: 600, color: overdue ? 'var(--danger)' : dueSoon ? '#f59e0b' : 'var(--text-3)' }}>
                              {overdue ? `${days}d overdue` : days === 0 ? 'Due today' : `${Math.abs(days)}d left`}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td><MemoStatusBadge status={row.displayStatus} /></td>
                    <td className="col-actions" onClick={e => e.stopPropagation()}>
                      {row.displayStatus === 'active' && (
                        <div className="action-row">
                          <RowActionsMenu
                            onExtend={row.legacyItem ? undefined : () => setExtendTarget(row)}
                            onReturn={() => openReturn(row)}
                            onDelete={row.legacyItem ? undefined : () => { setDeleteError(''); setDeleteTarget(row); }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {printAll && sortedFiltered.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={9}>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                      <span style={{ color: 'var(--text-2)' }}>
                        Total ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{footerTotals.ct.toFixed(2)}</strong>
                      </span>
                      <span style={{ color: 'var(--text-2)' }}>
                        Total Amount <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.amount)}</strong>
                      </span>
                      <span style={{ color: 'var(--text-2)' }}>
                        Avg. Price/ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.avgPricePerCt)}</strong>
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="no-print">
          <Pagination
            current={currentPage} total={totalPages}
            totalItems={sortedFiltered.length} pageSize={PAGE_SIZE}
            onChange={setCurrentPage}
          />
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title="Delete this memo?"
        confirmLabel="Delete"
        loading={deleting}
        error={deleteError}
        message={deleteTarget && (
          <>
            {deleteTarget.data.code ? `Code ${deleteTarget.data.code}` : deleteTarget.data.shape}, {deleteTarget.data.carat.toFixed(2)} ct to {entityName(deleteTarget.data.entityId)}.
            <br />Use this only for a parcel added by mistake. It is removed completely and the carats are free again. Nothing is kept in the history.
          </>
        )}
      />

      <ReturnModal
        open={!!returnTarget}
        onClose={() => setReturnTarget(null)}
        item={returnTarget?.legacyItem}
        memo={returnTarget?.legacyItem ? undefined : returnTarget?.data}
        entities={entities}
      />

      {extendTarget && !extendTarget.legacyItem && (
        <ExtendMemoModal memo={extendTarget.data} onClose={() => setExtendTarget(null)} />
      )}

      <MemoDetailModal
        row={detailRow}
        onClose={() => setDetailRow(null)}
        onReturn={row => { setDetailRow(null); openReturn(row); }}
        entities={entities}
      />
      </>
      )}

      <SendMemoModal
        open={showSendMemo}
        onClose={() => setShowSendMemo(false)}
        available={sendMemoAvailable}
        customers={customers}
        onSaved={memo => setNewMemo(memo)}
      />
      <ConsignmentPreviewModal
        open={!!newMemo}
        onClose={() => setNewMemo(null)}
        memo={newMemo}
        entity={newMemo ? entities.find(e => e.id === newMemo.toEntityId) : undefined}
        settings={settings}
        business={business}
      />
    </AppLayout>
  );
}
